/**
 * Module: `export/docx-hyperlinks.ts` — ensure Word hyperlinks look and act clickable.
 * @m2d creates ExternalHyperlink runs with underline only; we attach the Hyperlink
 * character style + explicit blue color so Word/LibreOffice treat them as real links.
 */

import { enhanceDocxDisclaimer } from "@/lib/export/docx-disclaimer";
import { enhanceDocxFieldLabels } from "@/lib/export/docx-field-labels";
import { enhanceDocxSourceSeparators } from "@/lib/export/docx-source-separators";
import { EXPORT_COLOR_LINK } from "@/lib/export/export-styles";

function enhanceHyperlinkBlock(block: string): string {
  const displayText = Array.from(
    block.matchAll(/<w:t\b[^>]*>([\s\S]*?)<\/w:t>/g)
  )
    .map((match) => match[1] ?? "")
    .join("")
    .trim();
  const isBareUrlLink = /^https?:\/\//i.test(displayText);

  return block.replace(
    /<w:r\b([^>]*)>([\s\S]*?)<\/w:r>/g,
    (_run, attrs, inner) => {
      if (/<w:rPr>/.test(inner)) {
        const nextInner = inner.replace(
          /<w:rPr>([\s\S]*?)<\/w:rPr>/,
          (_m, rprInner) => {
            let patched = String(rprInner);
            if (!patched.includes("w:rStyle")) {
              patched = `<w:rStyle w:val="Hyperlink"/>${patched}`;
            }
            if (!patched.includes("<w:color ")) {
              patched += `<w:color w:val="${EXPORT_COLOR_LINK}"/>`;
            }
            if (!patched.includes("<w:u ")) {
              patched += `<w:u w:val="single" w:color="${EXPORT_COLOR_LINK}"/>`;
            } else {
              // Ensure underline carries the link color.
              patched = patched.replace(
                /<w:u\b[^/]*\/>/g,
                `<w:u w:val="single" w:color="${EXPORT_COLOR_LINK}"/>`
              );
            }
            // Document titles are linked with a label (not a bare URL) → keep bold.
            if (!isBareUrlLink && !/<w:b\b/.test(patched)) {
              patched += `<w:b/>`;
            }
            return `<w:rPr>${patched}</w:rPr>`;
          }
        );
        return `<w:r${attrs}>${nextInner}</w:r>`;
      }
      const titleBold = isBareUrlLink ? "" : "<w:b/>";
      return `<w:r${attrs}><w:rPr><w:rStyle w:val="Hyperlink"/>${titleBold}<w:color w:val="${EXPORT_COLOR_LINK}"/><w:u w:val="single" w:color="${EXPORT_COLOR_LINK}"/></w:rPr>${inner}</w:r>`;
    }
  );
}

/**
 * Patches a generated DOCX so every hyperlink run uses the Hyperlink character style,
 * and bold field labels ending with `:` are underlined (parity with PDF).
 */
export async function enhanceDocxHyperlinks(blob: Blob): Promise<Blob> {
  const JSZip = (await import("jszip")).default;
  const zip = await JSZip.loadAsync(await blob.arrayBuffer());
  const docFile = zip.file("word/document.xml");
  if (!docFile) return blob;

  const xml = await docFile.async("string");
  const withLinks = xml.replace(
    /<w:hyperlink\b[^>]*>[\s\S]*?<\/w:hyperlink>/g,
    (block) => enhanceHyperlinkBlock(block)
  );
  const withLabels = enhanceDocxFieldLabels(withLinks);
  const withDisclaimer = enhanceDocxDisclaimer(withLabels);
  const patched = enhanceDocxSourceSeparators(withDisclaimer);

  if (patched === xml) return blob;

  zip.file("word/document.xml", patched);
  return zip.generateAsync({
    type: "blob",
    mimeType:
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  });
}
